import { c as _c } from "react/compiler-runtime";
import { HeadsetHelp, WarningCircleSolid } from '@byway/icons';
import { useTranslations } from 'next-intl';
import type { Key } from 'react';
import { Button } from '~/components/Button';
import { Stack } from '~/components/Layout/Stack';
import { Link } from '~/components/Link';
import { Tag, TagGroup } from '~/components/TagGroup/src/TagGroup';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundPreferences, usePlaygroundSelectedDestinations, usePlaygroundUIState } from '~/stores/playgroundStore';
import { gtag } from '~/utils';
import { PlaygroundDestinationSelector } from '../PlaygroundDestinationSelector/PlaygroundDestinationSelector';
import styles from './DestinationsPanel.module.css';
export default function DestinationsPanel() {
  const $ = _c(35);
  const t = useTranslations();
  const playgroundContext = usePlaygroundContext();
  const {
    dpPlaces: dpSelectedPlaces
  } = usePlaygroundSelectedDestinations();
  const {
    unsupportedDestinations
  } = usePlaygroundPreferences();
  const {
    removeDestination,
    setRemovingDestination,
    closeDrawer,
    setContext,
    removeUnsupportedDestination
  } = usePlaygroundActions();
  const trpcRemoveDest = api.playground.removeDestination.useMutation();
  const playgroundUIState = usePlaygroundUIState();
  const {
    isAddingDestination,
    removingDestination
  } = playgroundUIState;
  const convertKeyToNumber = _temp;
  let t0;
  if ($[0] !== dpSelectedPlaces || $[1] !== playgroundContext || $[2] !== removeDestination || $[3] !== removeUnsupportedDestination || $[4] !== removingDestination || $[5] !== setContext || $[6] !== setRemovingDestination || $[7] !== trpcRemoveDest || $[8] !== unsupportedDestinations) {
    t0 = async ids => {
      ;
      try {
        if (!playgroundContext) {
          return;
        }
        setRemovingDestination(ids);
        const isUnavailable = unsupportedDestinations.some(place => ids.has(place.id));
        if (isUnavailable) {
          removeUnsupportedDestination(ids.values().next().value);
          setRemovingDestination(new Set());
          return;
        }
        await Promise.all(Array.from(ids).map(async id_0 => {
          const newContext = await trpcRemoveDest.mutateAsync({
            playgroundInputContext: playgroundContext,
            placeId: convertKeyToNumber(id_0)
          });
          gtag("event", "destination_unpinned", {
            event_category: "Playground",
            event_label: dpSelectedPlaces.find(place_0 => place_0.id === id_0)?.n,
            destination_id: convertKeyToNumber(id_0)
          });
          if (newContext) {
            setContext(newContext);
            removeDestination(convertKeyToNumber(id_0));
            const newSet = new Set(removingDestination);
            newSet.delete(id_0);
            setRemovingDestination(newSet);
          } else {
            throw new Error("no new context from JAI");
          }
        }));
      } catch (t1) {
        setRemovingDestination(new Set());
      }
    };
    $[0] = dpSelectedPlaces;
    $[1] = playgroundContext;
    $[2] = removeDestination;
    $[3] = removeUnsupportedDestination;
    $[4] = removingDestination;
    $[5] = setContext;
    $[6] = setRemovingDestination;
    $[7] = trpcRemoveDest;
    $[8] = unsupportedDestinations;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  const onRemoveHandler = t0;
  let t1;
  if ($[10] !== t) {
    t1 = t("playground.destination_heading");
    $[10] = t;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== t1) {
    t2 = <h2 className={styles.title}>{t1}</h2>;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== t || $[15] !== unsupportedDestinations.length) {
    t3 = unsupportedDestinations.length === 0 && <div className={styles.unreachablePlaces}><p>{t("content.unreachable_help")}</p></div>;
    $[14] = t;
    $[15] = unsupportedDestinations.length;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PlaygroundDestinationSelector />;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== dpSelectedPlaces || $[19] !== isAddingDestination || $[20] !== onRemoveHandler || $[21] !== removingDestination || $[22] !== t || $[23] !== trpcRemoveDest || $[24] !== unsupportedDestinations) {
    t5 = dpSelectedPlaces.length > 0 || isAddingDestination || unsupportedDestinations.length > 0 ? <><TagGroup label={unsupportedDestinations.length > 0 ? undefined : t("playground.places_selected")} onRemove={onRemoveHandler} aria-label="Destinations selected" selectionMode="none">{unsupportedDestinations.length === 0 && dpSelectedPlaces.map(place_1 => {
          const isRemoving = removingDestination?.has(place_1.id);
          return <Tag variant="clear" id={place_1.id} key={`${place_1.id}-selected-tag`} isDisabled={isRemoving}>{isRemoving ? t("content.feedback.place_remove_loading") : place_1.n}</Tag>;
        })}{unsupportedDestinations.map(_temp2)}{isAddingDestination && <Tag isDisabled={true} variant="clear" id="loading-tag" key="loading-tag">{t("content.feedback.place_add_loading")}</Tag>}</TagGroup><div className={styles.errorsContainer}>{trpcRemoveDest.error && <p className={styles.error}><WarningCircleSolid decorative={true} />{t("content.feedback.destination_remove_error")}</p>}</div></> : null;
    $[18] = dpSelectedPlaces;
    $[19] = isAddingDestination;
    $[20] = onRemoveHandler;
    $[21] = removingDestination;
    $[22] = t;
    $[23] = trpcRemoveDest;
    $[24] = unsupportedDestinations;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== closeDrawer || $[27] !== t || $[28] !== unsupportedDestinations.length) {
    t6 = unsupportedDestinations.length > 0 ? <section aria-labelledby="concierge-heading"><h3 id="concierge-heading" className="sr-only">Concierge assistance</h3><p className={styles.conciergeText}>{t("content.concierge_feedback")}</p><p className={styles.conciergeText}>{t("content.concierge_contact")}</p><Link href="/concierge" asButton="primary" size="fullWidth">{t("playground.contact_concierge")}</Link></section> : <Button onPress={closeDrawer} ariaLabel={t("aria.destinations_save")} size="fullWidth" data-ua-click="playground-finished" dataTestId="save-destinations-button">{t("playground.finished")}</Button>;
    $[26] = closeDrawer;
    $[27] = t;
    $[28] = unsupportedDestinations.length;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== t2 || $[31] !== t3 || $[32] !== t5 || $[33] !== t6) {
    t7 = <section aria-label="Destinations panel" className={styles.section}><div className={styles.destinationsPanel}><Stack variant="vertical" space="--by-space-300">{t2}{t3}{t4}{t5}{t6}</Stack></div></section>;
    $[30] = t2;
    $[31] = t3;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
}
function _temp2(place_2) {
  return <Tag variant="clear" id={place_2.id} key={`${place_2.id}-unavailable-tag`}>{place_2.n}<HeadsetHelp decorative={true} /></Tag>;
}
function _temp(id) {
  if (typeof id === "string") {
    return parseInt(id, 10);
  }
  if (typeof id === "bigint" || typeof id === "symbol") {
    return -1;
  }
  return id;
}